/**
 * ProfileMenu — the bottom-left profile 'S' avatar that becomes the
 * account menu in Settings V2 (Plan B). It REPLACES the old "gear/Settings"
 * access in the sidebar account dropdown for the V2 contour:
 *   • Settings  → opens the consolidated Settings V2 area (/settings-v2)
 *   • Logout    → authApi.logout() + clear local auth + redirect /login
 *
 * Theme toggling moved OUT of this menu (2026-06-18, founder spec): on
 * desktop it lives as an instant icon button in the ActivityBar rail directly
 * above this avatar (<RailThemeToggle>); on mobile (flat-mobile) it sits next
 * to this avatar in the CompanyTree footer. So the menu is Settings + Logout.
 *
 * The avatar is the signed-in user's initial (useAuthStore user).
 *
 * ADR-057 monochrome; English-only.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Compass, LogOut, Settings } from 'lucide-react';

import { env } from '../../env';
import { authApi, systemApi, systemKeys } from '../../lib/api';
import { useSubscriptionLimits, type SubscriptionLimitRow } from '../../lib/subscription-limits';
import { isOnboardingComplete } from '../onboarding/store';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

function initialOf(name: string | null | undefined, email: string | null | undefined): string {
  const src = (name && name.trim()) || (email && email.trim()) || '?';
  return src.charAt(0).toUpperCase();
}

// ── Mobile USAGE section ─────────────────────────────────────────────
// The top status panel is hidden on mobile (board/$boardId.lazy.tsx), so the
// same resource/usage numbers live here at the TOP of the account menu. Same
// hslHealth + remaining/used logic as the desktop panel (server-load.tsx):
// muted PASTEL ramp (low 34% saturation, NOT a bright traffic light); health
// is "remaining" for the Claude/Codex limit rows and "100 - used" for the
// CPU/RAM/Disk rows, so greener always means healthier.

/** Muted pastel health ramp: 0 = terracotta, 100 = sage. */
function hslHealth(health: number | null | undefined): string {
  if (health == null) return 'hsl(var(--color-foreground) / 0.2)';
  const hue = Math.round(1.4 * Math.max(0, Math.min(100, health)));
  return `hsl(${hue}, 34%, 54%)`;
}

/** REMAINING percentage (100 - used), clamped; null passes through. */
function remainingPct(usedPct: number | null | undefined): number | null {
  if (usedPct == null) return null;
  return Math.max(0, Math.min(100, 100 - usedPct));
}

/** One usage row: caption + thin 5px pastel bar + percent. */
function UsageRow({
  caption,
  pct,
  health,
}: {
  caption: string;
  /** Fill width 0-100, or null = unavailable. */
  pct: number | null;
  /** 0-100 health for the colour ramp, or null = unavailable. */
  health: number | null;
}) {
  const unavailable = pct == null;
  const text = unavailable ? '—' : `${Math.round(pct)}%`;
  return (
    <div className="flex items-center gap-2 px-2 py-0.5">
      <span className="shrink-0 text-[11px] text-muted-foreground" style={{ width: 78 }}>
        {caption}
      </span>
      <div
        role="progressbar"
        aria-valuenow={unavailable ? undefined : Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${caption} ${text}`}
        className="flex-1"
        style={{ height: 5, borderRadius: 3, background: 'hsl(var(--color-foreground) / 0.1)', overflow: 'hidden' }}
      >
        {!unavailable && (
          <div
            style={{
              width: `${Math.max(0, Math.min(100, pct))}%`,
              height: '100%',
              borderRadius: 3,
              backgroundColor: hslHealth(health),
            }}
          />
        )}
      </div>
      <span
        className={cn(
          'shrink-0 text-right text-[11px] font-medium tabular-nums',
          unavailable ? 'text-muted-foreground' : 'text-foreground',
        )}
        style={{ width: 30 }}
      >
        {text}
      </span>
    </div>
  );
}

/** The muted word a subscription row shows when it has no reading. */
function limitWord(row: SubscriptionLimitRow): string {
  switch (row.state) {
    case 'pending':
      return 'measuring…';
    case 'unsupported':
      return 'not reported';
    default:
      return 'not measured';
  }
}

/**
 * USAGE block for the mobile account menu — one entry per connected
 * subscription of the viewer (REMAINING; Claude 5h + wk, GPT wk), then
 * CPU/RAM/Disk (USED). Same sources as the desktop strip
 * (lib/subscription-limits.ts, /system/load).
 */
function UsageSection() {
  const loadQ = useQuery({
    queryKey: systemKeys.load(),
    queryFn: () => systemApi.load(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
  const limits = useSubscriptionLimits();

  const load = loadQ.data;
  // The optional chain has to reach the field being READ, not stop at the
  // object holding it. Until 2026-08-02 these three were `load?.cpu.usedPct` —
  // guarded on `load`, unguarded on `cpu` — so a /system/load answer carrying
  // `ram` but no `cpu` (an older gateway, a probe that timed out, a partial
  // read) threw here, and because this menu is mounted by the shell the
  // settings error boundary took the WHOLE screen from the account button.
  // Same class as the credentials crash fixed in the same commit: a field the
  // types promise and the wire does not.
  const cpu = load?.cpu?.usedPct ?? null;
  const ram = load?.ram?.usedPct ?? null;
  const disk = load?.disk?.usedPct ?? null;

  return (
    <div className="py-1">
      <DropdownMenuLabel className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Usage
      </DropdownMenuLabel>
      {limits.rows.map((row) => {
        const numbers = row.state === 'live' || row.state === 'stale';
        if (!numbers) {
          return (
            <div
              key={row.key}
              role="group"
              aria-label={row.name}
              data-limit-state={row.state}
              className="flex items-center justify-between gap-2 px-2 py-0.5"
            >
              <span className="shrink-0 text-[11px] text-muted-foreground">{row.name}</span>
              <span className="text-right text-[11px] text-muted-foreground">{limitWord(row)}</span>
            </div>
          );
        }
        const fiveHour = remainingPct(row.fiveHour?.usedPct);
        const weekly = remainingPct(row.weekly?.usedPct);
        return (
          <div key={row.key} role="group" aria-label={row.name} data-limit-state={row.state}>
            {row.vendor === 'anthropic' && (
              <UsageRow caption={`${row.name} · 5h`} pct={fiveHour} health={fiveHour} />
            )}
            <UsageRow caption={`${row.name} · wk`} pct={weekly} health={weekly} />
          </div>
        );
      })}
      {limits.status === 'ready' && limits.rows.length === 0 && (
        <div className="px-2 py-0.5 text-[11px] text-muted-foreground">No AI subscription connected</div>
      )}
      <UsageRow caption="CPU" pct={cpu} health={cpu == null ? null : 100 - cpu} />
      <UsageRow caption="RAM" pct={ram} health={ram == null ? null : 100 - ram} />
      <UsageRow caption="Disk" pct={disk} health={disk == null ? null : 100 - disk} />
    </div>
  );
}

interface ProfileMenuProps {
  /** flat-mobile renders a wider row; default = compact desktop avatar. */
  variant?: 'default' | 'flat-mobile';
}

export function ProfileMenu({ variant = 'default' }: ProfileMenuProps) {
  const user = useAuthStore((s) => s.user);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const isFlat = variant === 'flat-mobile';

  const handleSignOut = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore — proceed to clear local state */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  const initial = initialOf(user?.displayName, user?.email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          data-testid="profile-menu-trigger"
          className={cn(
            'flex items-center gap-2 rounded-md text-left transition-colors hover:bg-foreground/5',
            isFlat ? 'w-full px-3 py-2' : 'p-1',
          )}
        >
          <span
            aria-hidden
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-foreground/[0.06] text-[12px] font-semibold text-foreground"
          >
            {initial}
          </span>
          {isFlat && (
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-[13px] font-medium text-foreground">
                {user?.displayName ?? user?.email ?? 'Account'}
              </span>
              {user?.displayName && (
                <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
              )}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={isFlat ? 'start' : 'end'} side="top" className={isFlat ? 'w-64' : 'w-56'}>
        <DropdownMenuLabel>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
            <span className="text-xs text-muted-foreground">{user?.email}</span>
          </div>
        </DropdownMenuLabel>
        {/* USAGE — mobile only. The top status panel is hidden on mobile, so
            the same Claude/Codex (remaining) + CPU/RAM/Disk (used) numbers
            live here, above Settings / Logout, separated by a hairline. */}
        {isFlat && (
          <>
            <DropdownMenuSeparator />
            <UsageSection />
          </>
        )}
        <DropdownMenuSeparator />
        {/* SPEC-09 §3.5.1 manual entry — the ONLY way back into the first-run
            wizard, and it disappears the moment the wizard is finished: an
            entry that leads straight to a redirect is worse than no entry.
            Gated on the baked VITE_ONBOARDING flag, so on an OFF build this
            menu is byte-identical to the one before this feature existed.

            IT LIVES HERE AND NOT IN `components/layout/sidebar.tsx`. That file
            still carries a fuller-looking account dropdown and is imported by
            NOTHING — measured 2026-08-07, and measured only because the browser
            proof for this item went red against a menu that had no Members
            entry in it. THIS is the account menu a person opens, on desktop
            (through ActivityBar) and on mobile (mobile-account-footer). */}
        {env.VITE_ONBOARDING && !isOnboardingComplete(user?.id ?? null) && (
          <DropdownMenuItem asChild data-testid="setup-guide-item">
            <Link to="/welcome">
              <Compass className="h-4 w-4" />
              <span>Setup guide</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/settings-v2">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
